import { useMutation, useQuery } from "@tanstack/react-query";
import { loginApi, logoutApi, getCurrentUserApi } from "../model/authApi";
import type { LoginCredentials, User } from "../model/authTypes";
import { QUERY_KEYS } from "@/constants/queryKeys";

/**
 * Auth ViewModel
 * Manages authentication state and business logic
 */
export const useAuthViewModel = () => {
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => loginApi(credentials, "central"),
  });

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
  });

  const {
    data: user,
    isLoading,
    error,
  } = useQuery<User>({
    queryKey: [QUERY_KEYS.USER],
    queryFn: getCurrentUserApi,
    enabled: false, // Only fetch when explicitly called
  });

  const login = async (credentials: LoginCredentials) => {
    return loginMutation.mutateAsync(credentials);
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  return {
    user: loginMutation.data || user || null,
    isLoading: loginMutation.isPending || logoutMutation.isPending || isLoading,
    error: loginMutation.error || logoutMutation.error || error,
    isSuccess: loginMutation.isSuccess,
    isAuthenticated: !!loginMutation.data || !!user,
    login,
    logout,
  };
};
